import { useEffect, useState } from "react";
import ReactQuill from "react-quill";

import "./TextEditor.scss";

let textEditorCount = 0;

type Props = {
  id?: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  showToolbar?: boolean;
};

function TextEditor({
  id = "texteditor",
  value,
  placeholder = "",
  onChange,
  showToolbar = true
}: Props) {
  const [toolbarId, setToolbarId] = useState("");

  useEffect(() => {
    setToolbarId(`${id}-toolbar-${textEditorCount++}`);
  }, [id]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    const checkTooltip = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Debounce with 10ms to balance performance and responsiveness
      timeoutId = setTimeout(() => {
        const el = document.querySelector<HTMLElement>('[data-mode="link"]');
        if (!el) { return; }

        requestAnimationFrame(() => {
          // This logic is added to keep the link tooltip within the container
          const rect = el.getBoundingClientRect();
          const container = el.closest('.ql-container') as HTMLElement;
          if (!container) { return; }

          const containerRect = container.getBoundingClientRect();
          const overflowLeft = rect.left - containerRect.left;
          const overflowRight = rect.right - containerRect.right;

          if (overflowLeft < 0) {
            const currentLeft = parseFloat(el.style.left || '0');
            el.style.left = `${currentLeft - overflowLeft}px`;
            el.style.transform = 'translateX(0)';
          }

          if (overflowRight > 0) {
            const currentLeft = parseFloat(el.style.left || '0');
            el.style.left = `${currentLeft - overflowRight}px`;
          }
        });
      }, 10);
    };

    const observer = new MutationObserver(checkTooltip);

    const editorContainer = document.getElementById(id);
    const observeTarget = editorContainer || document.querySelector(`#${id}`) || document.body;

    observer.observe(observeTarget, {
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!toolbarId) {
    return <></>;
  }

  return (
    <div className="text-editor">
      {showToolbar && (
        <div id={toolbarId} className="mb-3">
          <button className="ql-bold" />
          <button className="ql-italic" />
          <button className="ql-underline" />
          <button className="ql-link" />
          <button className="ql-list" value="bullet" />
        </div>
      )}

      <ReactQuill
        id={id}
        className=""
        theme="snow"
        onChange={onChange}
        modules={{
          toolbar: showToolbar
            ? {
              container: `#${toolbarId}`
            }
            : false
        }}
        placeholder={placeholder}
        value={value}
      />
    </div>
  );
}

export default TextEditor;